import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Search, FileText, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Action badge component
const ActionBadge = ({ action }) => {
  const config = {
    created: { bg: "bg-green-600", text: "CREATE" },
    create: { bg: "bg-green-600", text: "CREATE" },
    status_change: { bg: "bg-amber-600", text: "UPDATE" },
    update: { bg: "bg-amber-600", text: "UPDATE" },
    file_upload: { bg: "bg-blue-600", text: "UPLOAD" },
    upload: { bg: "bg-blue-600", text: "UPLOAD" },
    approval: { bg: "bg-purple-600", text: "APPROVE" },
    file_approval: { bg: "bg-purple-600", text: "REVIEW" },
    revision: { bg: "bg-amber-600", text: "REVISION" },
    delete: { bg: "bg-red-600", text: "DELETE" },
  };
  
  const { bg, text } = config[action] || { bg: "bg-gray-600", text: action?.toUpperCase() };
  
  return (
    <span className={`px-2 py-1 ${bg} text-white text-xs font-semibold rounded`}>
      {text}
    </span>
  );
};

// Resource badge component
const ResourceBadge = ({ resource }) => {
  return (
    <span className="px-2 py-1 bg-secondary text-foreground text-xs font-medium rounded border border-border">
      {resource}
    </span>
  );
};

// Summary card component
const SummaryCard = ({ title, children }) => (
  <div className="bg-card border border-border rounded-xl p-4">
    <p className="text-sm text-muted-foreground mb-2">{title}</p>
    {children}
  </div>
);

export const AuditTrailPage = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filters, setFilters] = useState({
    action: "",
    resource: "",
    user: "",
  });
  const [page, setPage] = useState(1);
  const [stats, setStats] = useState({
    last24Hours: 0,
    byAction: {},
    byResource: {},
    topUsers: {},
  });
  const pageSize = 20;

  const fetchActivities = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const res = await axios.get(`${API}/audit-trail`, {
        params: {
          action: filters.action || undefined,
          resource: filters.resource || undefined,
          user: filters.user || undefined,
          page,
          page_size: pageSize,
        },
      });
      setActivities(res.data.activities || []);
      setStats(res.data.stats || {});
    } catch (err) {
      toast.error("Failed to load audit trail");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchActivities();
  }, [filters, page]);

  const handleRefresh = () => {
    fetchActivities(true);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const uniqueActions = [...new Set(activities.map((a) => a.activity_type))];
  const uniqueResources = [...new Set(activities.map((a) => a.resource_type).filter(Boolean))];

  if (loading) {
    return <div className="animate-pulse text-muted-foreground">Loading audit trail...</div>;
  }

  return (
    <div className="animate-fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText size={28} />
            Audit Trail
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all user interactions with the system
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-secondary hover:bg-secondary/80 rounded-lg transition-colors"
        >
          <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <SummaryCard title="Last 24 Hours">
          <div className="flex items-center gap-2">
            <span className="text-3xl font-bold">{stats.last24Hours || 0}</span>
            <TrendingUp size={24} className="text-blue-500" />
          </div>
        </SummaryCard>

        <SummaryCard title="By Action">
          <div className="flex flex-wrap gap-1">
            {Object.entries(stats.byAction || {}).map(([action, count]) => (
              <span
                key={action}
                className="px-2 py-1 bg-secondary text-xs font-medium rounded"
              >
                {action.toUpperCase()}: {count}
              </span>
            ))}
            {Object.keys(stats.byAction || {}).length === 0 && (
              <span className="text-muted-foreground text-sm">No data</span>
            )}
          </div>
        </SummaryCard>

        <SummaryCard title="By Resource">
          <div className="flex flex-wrap gap-1">
            {Object.entries(stats.byResource || {}).map(([resource, count]) => (
              <span
                key={resource}
                className="px-2 py-1 bg-secondary text-xs font-medium rounded"
              >
                {resource}: {count}
              </span>
            ))}
            {Object.keys(stats.byResource || {}).length === 0 && (
              <span className="text-muted-foreground text-sm">No data</span>
            )}
          </div>
        </SummaryCard>

        <SummaryCard title="Top Users">
          <div className="flex flex-wrap gap-1">
            {Object.entries(stats.topUsers || {})
              .slice(0, 3)
              .map(([user, count]) => (
                <span
                  key={user}
                  className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded"
                >
                  {user}: {count}
                </span>
              ))}
            {Object.keys(stats.topUsers || {}).length === 0 && (
              <span className="text-muted-foreground text-sm">No data</span>
            )}
          </div>
        </SummaryCard>
      </div>

      {/* Filters */}
      <div className="bg-card border border-border rounded-xl p-4 mb-6">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">All Actions</option>
            {uniqueActions.map((action) => (
              <option key={action} value={action}>
                {action.replace("_", " ").toUpperCase()}
              </option>
            ))}
          </select>

          <select
            value={filters.resource}
            onChange={(e) => setFilters({ ...filters, resource: e.target.value })}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-ring"
          >
            <option value="">All Resources</option>
            {uniqueResources.map((resource) => (
              <option key={resource} value={resource}>
                {resource}
              </option>
            ))}
          </select>

          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by user email..."
              value={filters.user}
              onChange={(e) => setFilters({ ...filters, user: e.target.value })}
              className="w-full pl-9 pr-4 py-2 bg-background border border-border rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
      </div>

      {/* Activity Log Table */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-semibold">Activity Log</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Timestamp</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Action</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Resource</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">User</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Details</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((activity, idx) => (
                <tr
                  key={activity.id || idx}
                  className="border-b border-border last:border-0 hover:bg-secondary/30"
                >
                  <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                    {formatTimestamp(activity.created_at)}
                  </td>
                  <td className="px-4 py-3">
                    <ActionBadge action={activity.activity_type} />
                  </td>
                  <td className="px-4 py-3">
                    <ResourceBadge resource={activity.resource_type || "project"} />
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{activity.user_name}</p>
                      <p className="text-xs text-muted-foreground">{activity.user_role || "User"}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{activity.description}</p>
                    {activity.details && (
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {typeof activity.details === "object"
                          ? Object.entries(activity.details)
                              .map(([k, v]) => `${k}: ${v}`)
                              .join(", ")
                          : activity.details}
                      </p>
                    )}
                  </td>
                </tr>
              ))}
              {activities.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    No activities found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Page {page} of {Math.ceil(activities.length / pageSize) || 1}
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 bg-secondary hover:bg-secondary/80 rounded text-sm disabled:opacity-50"
            >
              ‹
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={activities.length < pageSize}
              className="px-3 py-1 bg-secondary hover:bg-secondary/80 rounded text-sm disabled:opacity-50"
            >
              ›
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
